import * as React from 'react';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonIcon from '@mui/icons-material/Person';
import HistoryIcon from '@mui/icons-material/History';
import BadgeIcon from '@mui/icons-material/Badge';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import HotelIcon from '@mui/icons-material/Hotel';
import StarIcon from '@mui/icons-material/Star';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import HomeIcon from '@mui/icons-material/Home';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import moment from 'moment';
import { getApi } from 'views/services/api';
import DeleteCustomer from '../DeleteCustomer';
import AddCustomer from '../AddCustomer';
import EditCustomer from '../EditCustomer';
import {
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Avatar, Skeleton, Tooltip
} from '@mui/material';

// ─── Styled Components ────────────────────────────────────────────────────────

const DashboardWrapper = styled(Box)(() => ({
  fontFamily: '"Outfit", "Segoe UI", sans-serif',
  background: '#f4f6f8',
  minHeight: '100vh',
  padding: '24px',
}));

const HeaderCard = styled(Paper)(() => ({
  background: 'linear-gradient(135deg, #1565c0 0%, #1e88e5 60%, #42a5f5 100%)',
  borderRadius: '20px',
  padding: '28px 32px',
  marginBottom: '24px',
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  boxShadow: '0 8px 32px rgba(21, 101, 192, 0.3)',
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '-40px',
    right: '-40px',
    width: '200px',
    height: '200px',
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.08)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    bottom: '-60px',
    right: '80px',
    width: '150px',
    height: '150px',
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.05)',
  },
}));

const CustomerAvatar = styled(Avatar)(() => ({
  width: 68,
  height: 68,
  background: 'rgba(255,255,255,0.2)',
  border: '3px solid rgba(255,255,255,0.4)',
  fontSize: '1.8rem',
  fontWeight: 700,
  backdropFilter: 'blur(8px)',
}));

const StyledTabList = styled(TabList)(() => ({
  background: '#fff',
  borderRadius: '14px',
  padding: '6px',
  boxShadow: '0 2px 12px rgba(21,101,192,0.10)',
  minHeight: 'unset',
  '& .MuiTabs-indicator': {
    display: 'none',
  },
}));

const StyledTab = styled(Tab)(() => ({
  borderRadius: '10px',
  minHeight: '42px',
  fontWeight: 600,
  fontSize: '0.875rem',
  textTransform: 'none',
  color: '#6b7280',
  transition: 'all 0.2s ease',
  '&.Mui-selected': {
    background: 'linear-gradient(135deg, #1565c0, #1e88e5)',
    color: '#fff !important',
    boxShadow: '0 4px 12px rgba(21,101,192,0.35)',
  },
}));

const InfoCard = styled(Paper)(() => ({
  borderRadius: '18px',
  padding: '28px 32px',
  background: '#fff',
  boxShadow: '0 2px 16px rgba(21,101,192,0.08)',
  border: '1px solid rgba(30,136,229,0.08)',
}));

const InfoField = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '14px',
  padding: '16px',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, #f8fafd, #edf2f9)',
  border: '1px solid rgba(30,136,229,0.1)',
  transition: 'box-shadow 0.2s',
  '&:hover': {
    boxShadow: '0 4px 16px rgba(21,101,192,0.12)',
  },
}));

const FieldIcon = styled(Box)(() => ({
  width: 38,
  height: 38,
  borderRadius: '10px',
  background: 'linear-gradient(135deg, #1565c0, #1e88e5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  '& svg': {
    fontSize: '1.1rem',
    color: '#fff',
  },
}));

const SectionTitle = styled(Typography)(() => ({
  fontWeight: 700,
  fontSize: '1.2rem',
  color: '#0a1929',
  marginBottom: '20px',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  '&::after': {
    content: '""',
    flex: 1,
    height: '2px',
    background: 'linear-gradient(to right, rgba(30,136,229,0.3), transparent)',
    borderRadius: '2px',
  },
}));

const StyledTableContainer = styled(TableContainer)(() => ({
  borderRadius: '14px',
  overflow: 'hidden',
  boxShadow: 'none',
  border: '1px solid rgba(30,136,229,0.1)',
}));

const StyledTableHead = styled(TableHead)(() => ({
  background: 'linear-gradient(135deg, #1565c0 0%, #1e88e5 100%)',
  '& .MuiTableCell-root': {
    color: '#fff',
    fontWeight: 700,
    fontSize: '0.8rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: 'none',
    padding: '14px 16px',
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  transition: 'background 0.15s',
  '&:nth-of-type(even)': {
    background: 'rgba(30,136,229,0.03)',
  },
  '&:hover': {
    background: 'rgba(30,136,229,0.07)',
  },
  '& .MuiTableCell-root': {
    borderBottom: '1px solid rgba(30,136,229,0.07)',
    fontSize: '0.875rem',
    color: '#374151',
    padding: '13px 16px',
  },
}));

const BackButton = styled(Button)(() => ({
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.875rem',
  borderColor: 'rgba(255,255,255,0.5)',
  color: '#fff',
  '&:hover': {
    borderColor: '#fff',
    background: 'rgba(255,255,255,0.12)',
  },
}));

const ViewIdButton = styled(Button)(() => ({
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.8rem',
  background: 'linear-gradient(135deg, #1565c0, #1e88e5)',
  color: '#fff',
  boxShadow: '0 3px 10px rgba(21,101,192,0.3)',
  '&:hover': {
    background: 'linear-gradient(135deg, #0d47a1, #1976d2)',
    boxShadow: '0 5px 16px rgba(21,101,192,0.45)',
  },
}));

// ─── Status Chip helper ───────────────────────────────────────────────────────

const statusConfig = {
  'checked-in':  { label: 'Activo',       color: '#16a34a', bg: '#dcfce7' },
  'checked-out': { label: 'Finalizado',   color: '#6b7280', bg: '#f3f4f6' },
  'reserved':    { label: 'Reservado',    color: '#1e88e5', bg: '#e3f2fd' },
  'cancelled':   { label: 'Cancelado',    color: '#dc2626', bg: '#fee2e2' },
};

const StatusChip = ({ status }) => {
  const cfg = statusConfig[status?.toLowerCase()] || { label: status || 'N/A', color: '#6b7280', bg: '#f3f4f6' };
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{
        fontWeight: 700,
        fontSize: '0.75rem',
        color: cfg.color,
        background: cfg.bg,
        border: `1px solid ${cfg.color}30`,
      }}
    />
  );
};

// ─── Info Field component ─────────────────────────────────────────────────────

const InfoItem = ({ icon, label, value }) => (
  <InfoField>
    <FieldIcon>{icon}</FieldIcon>
    <Box>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#1e88e5', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.3 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.95rem', color: '#0a1929', fontWeight: 500 }}>
        {value || 'N/A'}
      </Typography>
    </Box>
  </InfoField>
);

// ─── Loyalty (Fase 5.1) ────────────────────────────────────────────────────

const TIER_STYLES = {
  Bronze: { bg: 'linear-gradient(135deg,#a16207,#ca8a04)', text: '#fff', label: 'Bronce' },
  Silver: { bg: 'linear-gradient(135deg,#94a3b8,#cbd5e1)', text: '#1f2937', label: 'Plata' },
  Gold:   { bg: 'linear-gradient(135deg,#ca8a04,#fcd34d,#facc15)', text: '#1f2937', label: 'Oro' },
};

const TYPE_META = {
  fnb_consumption: { label: 'Consumo F&B', icon: <RestaurantIcon sx={{ fontSize: 18 }} />, color: '#ea580c' },
  stay:            { label: 'Estadía',    icon: <HotelIcon sx={{ fontSize: 18 }} />,      color: '#1e88e5' },
  bonus_admin:     { label: 'Bonus admin', icon: <StarIcon sx={{ fontSize: 18 }} />,      color: '#7c3aed' },
  redemption:      { label: 'Canje',       icon: <EmojiEventsIcon sx={{ fontSize: 18 }} />,color: '#dc2626' },
  adjustment:      { label: 'Ajuste',      icon: <BadgeIcon sx={{ fontSize: 18 }} />,      color: '#6b7280' },
};

const LoyaltyBadge = ({ tier, points }) => {
  const style = TIER_STYLES[tier] || TIER_STYLES.Bronze;
  return (
    <Box
      sx={{
        background: style.bg,
        color: style.text,
        borderRadius: 3,
        p: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 3,
        boxShadow: '0 6px 24px rgba(0,0,0,0.10)',
      }}
    >
      <EmojiEventsIcon sx={{ fontSize: 56 }} />
      <Box>
        <Typography sx={{ fontSize: '0.85rem', opacity: 0.85, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Tier actual
        </Typography>
        <Typography sx={{ fontSize: '2rem', fontWeight: 700, lineHeight: 1 }}>
          {style.label}
        </Typography>
      </Box>
      <Box sx={{ ml: 'auto', textAlign: 'right' }}>
        <Typography sx={{ fontSize: '0.85rem', opacity: 0.85 }}>Puntos acumulados</Typography>
        <Typography sx={{ fontSize: '2.4rem', fontWeight: 700, lineHeight: 1 }}>
          {Number(points).toLocaleString()}
        </Typography>
      </Box>
    </Box>
  );
};

const LoyaltyEventRow = ({ event }) => {
  const meta = TYPE_META[event.type] || TYPE_META.adjustment;
  const isNegative = Number(event.points) < 0;
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: '1px solid #eef0f3',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      <Avatar sx={{ bgcolor: meta.color + '20', color: meta.color, width: 36, height: 36 }}>
        {meta.icon}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.92rem', fontWeight: 600 }}>{meta.label}</Typography>
        <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>
          {event.description || '(sin descripción)'}
        </Typography>
        <Typography sx={{ fontSize: '0.7rem', color: '#9ca3af', mt: 0.3 }}>
          {moment(event.createdAt).format('DD/MM/YYYY HH:mm')} · origen {event.sourceSystem}
          {event.amountUsd ? ` · USD ${Number(event.amountUsd).toFixed(2)}` : ''}
        </Typography>
      </Box>
      <Chip
        size="small"
        label={`${isNegative ? '' : '+'}${event.points} pts`}
        sx={{
          fontWeight: 700,
          bgcolor: isNegative ? '#fee2e2' : '#dcfce7',
          color: isNegative ? '#b91c1c' : '#15803d',
        }}
      />
    </Paper>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

export default function CustomerDashboard() {
  const navigate = useNavigate();
  const [value, setValue] = useState('1');
  const [openEdit, setOpenEdit] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const hotel = JSON.parse(localStorage.getItem('hotelData'));

  const handleChange = (event, newValue) => setValue(newValue);

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);
  const handleOpenEditlead = () => setOpenEdit(true);
  const handleCloseEditlead = () => setOpenEdit(false);
  const handleOpenDeleteCustomer = () => setDeleteDialogOpen(true);
  const handleCloseDeleteLead = () => setDeleteDialogOpen(false);

  const [customerData, setCustomerData] = useState({});
  const [customerObjId, setCustomerObjId] = useState(null);
  const [reservationHistory, setReservationHistory] = useState([]);
  const [loyalty, setLoyalty] = useState(null); // { totalPoints, tier, events: [] }
  const [loyaltyLoading, setLoyaltyLoading] = useState(false);

  const params = useParams();
  const id = params.id;
  console.log('hmm =>', params);

  const fetchCustomerData = async () => {
    try {
      setLoading(true);
      const response = await getApi(`api/customer/viewcustomer/${id}?hotelId=${hotel?.hotelId}`);
      console.log('in information section response ==>', response);
      const customer = response?.data?.data;
      setCustomerData(customer);
      setCustomerObjId(customer?._id);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerReservationHistory = async () => {
    if (!customerObjId) return;
    try {
      console.log('Fetching customer reservation history...');
      console.log('url hit ===>', `api/customer/history/${customerObjId}?hotelId=${hotel?.hotelId}`);
      const response = await getApi(`api/customer/history/${customerObjId}?hotelId=${hotel?.hotelId}`);
      console.log('in history section response  ==>', response);
      setReservationHistory(response?.data || []);
    } catch (error) {
      console.log('error ==>', error);
    }
  };
  console.log('reservationHistory =>>', reservationHistory);

  useEffect(() => {
    fetchCustomerData();
  }, [openAdd, openEdit, deleteDialogOpen]);

  useEffect(() => {
    fetchCustomerReservationHistory();
  }, [customerObjId]);

  // Fase 5.1 — Fidelidad cruzada
  const fetchLoyalty = async () => {
    if (!customerObjId) return;
    try {
      setLoyaltyLoading(true);
      const response = await getApi(`api/customer/${customerObjId}/loyalty/events?limit=50`);
      if (response?.data?.totalPoints !== undefined) {
        setLoyalty(response.data);
      }
    } catch (e) {
      console.error('Loyalty fetch error:', e);
    } finally {
      setLoyaltyLoading(false);
    }
  };

  useEffect(() => {
    fetchLoyalty();
  }, [customerObjId]);

  const handleNavigate = (id) => {
    console.log('handleNavigate id ==>', id);
    navigate(`/dashboard/customer/view/specificbooking/${id}`);
  };

  const getInitials = () => {
    const first = customerData?.firstName?.[0] || '';
    const last = customerData?.lastName?.[0] || '';
    return (first + last).toUpperCase() || '?';
  };

  return (
    <>
      <DeleteCustomer open={deleteDialogOpen} handleClose={handleCloseDeleteLead} />
      <AddCustomer open={openAdd} handleClose={handleCloseAdd} />
      <EditCustomer open={openEdit} handleClose={handleCloseEditlead} data={customerData} />

      <DashboardWrapper>
        {/* ── Header ── */}
        <HeaderCard elevation={0}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, zIndex: 1 }}>
            <CustomerAvatar>
              {loading ? <PersonIcon /> : getInitials()}
            </CustomerAvatar>
            <Box>
              {loading ? (
                <>
                  <Skeleton variant="text" width={180} sx={{ bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 1 }} />
                  <Skeleton variant="text" width={120} sx={{ bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 1 }} />
                </>
              ) : (
                <>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.4rem', lineHeight: 1.2, color: '#fff' }}>
                    {customerData?.firstName || 'Cliente'} {customerData?.lastName || ''}
                  </Typography>
                  <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.75)', mt: 0.4 }}>
                    {customerData?.email || 'Sin correo registrado'}
                  </Typography>
                </>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, zIndex: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Tooltip title="Editar cliente">
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleOpenEditlead}
                sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 600, borderColor: 'rgba(255,255,255,0.5)', color: '#fff', '&:hover': { borderColor: '#fff', background: 'rgba(255,255,255,0.12)' } }}
              >
                Editar
              </Button>
            </Tooltip>
            <Tooltip title="Eliminar cliente">
              <Button
                variant="outlined"
                startIcon={<DeleteIcon />}
                onClick={handleOpenDeleteCustomer}
                sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 600, borderColor: 'rgba(255,100,100,0.5)', color: '#ffaaaa', '&:hover': { borderColor: '#ff8888', background: 'rgba(255,80,80,0.12)' } }}
              >
                Eliminar
              </Button>
            </Tooltip>
            <BackButton variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
              Volver
            </BackButton>
          </Box>
        </HeaderCard>

        {/* ── Tabs ── */}
        <Box sx={{ width: '100%', typography: 'body1' }}>
          <TabContext value={value}>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <StyledTabList onChange={handleChange} aria-label="tabs de cliente">
                <StyledTab icon={<PersonIcon sx={{ fontSize: '1rem' }} />} iconPosition="start" label="Información" value="1" />
                <StyledTab icon={<HistoryIcon sx={{ fontSize: '1rem' }} />} iconPosition="start" label="Historial" value="2" />
                <StyledTab
                  icon={<EmojiEventsIcon sx={{ fontSize: '1rem' }} />}
                  iconPosition="start"
                  label={`Fidelidad${loyalty?.totalPoints ? ` · ${loyalty.totalPoints}` : ''}`}
                  value="3"
                />
              </StyledTabList>
            </Box>

            {/* ── Tab: Información ── */}
            <TabPanel value="1" sx={{ p: 0 }}>
              <InfoCard elevation={0}>
                <SectionTitle>
                  <PersonIcon sx={{ color: '#1e88e5', fontSize: '1.3rem' }} />
                  Datos del Cliente
                </SectionTitle>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <InfoItem icon={<PersonIcon />} label="Nombre" value={customerData?.firstName} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoItem icon={<PersonIcon />} label="Apellido" value={customerData?.lastName} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoItem icon={<EmailIcon />} label="Correo Electrónico" value={customerData?.email} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoItem icon={<HomeIcon />} label="Dirección" value={customerData?.address} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoItem icon={<BadgeIcon />} label="Tipo de Documento" value={customerData?.idCardType} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoItem icon={<CreditCardIcon />} label="Número de Documento" value={customerData?.idcardNumber} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoItem icon={<PhoneIcon />} label="Teléfono" value={customerData?.phoneNumber} />
                  </Grid>

                  {/* ID Proof buttons */}
                  <Grid item xs={12} sm={6}>
                    <InfoField sx={{ flexDirection: 'column', gap: 1 }}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#1e88e5', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Documento de Identidad
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <a href={customerData?.idFile} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                          <ViewIdButton size="small" startIcon={<VisibilityIcon sx={{ fontSize: '0.9rem !important' }} />}>
                            Ver Frente
                          </ViewIdButton>
                        </a>
                        <a href={customerData?.idFile2} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                          <ViewIdButton size="small" startIcon={<VisibilityIcon sx={{ fontSize: '0.9rem !important' }} />}>
                            Ver Reverso
                          </ViewIdButton>
                        </a>
                      </Box>
                    </InfoField>
                  </Grid>
                </Grid>
              </InfoCard>
            </TabPanel>

            {/* ── Tab: Historial ── */}
            <TabPanel value="2" sx={{ p: 0 }}>
              <InfoCard elevation={0}>
                <SectionTitle>
                  <HistoryIcon sx={{ color: '#1e88e5', fontSize: '1.3rem' }} />
                  Historial de Reservaciones
                </SectionTitle>

                <StyledTableContainer component={Paper} elevation={0}>
                  <Table sx={{ minWidth: 650 }} aria-label="historial de reservaciones">
                    <StyledTableHead>
                      <TableRow>
                        <TableCell>Habitación</TableCell>
                        <TableCell>Fecha Entrada</TableCell>
                        <TableCell>Fecha Salida</TableCell>
                        <TableCell>Anticipo</TableCell>
                        <TableCell>Total</TableCell>
                        <TableCell>Restante</TableCell>
                        <TableCell>Estado</TableCell>
                      </TableRow>
                    </StyledTableHead>
                    <TableBody>
                      {reservationHistory.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 5, color: '#9ca3af' }}>
                            <HistoryIcon sx={{ fontSize: 40, color: '#d1d5db', mb: 1, display: 'block', mx: 'auto' }} />
                            Sin historial de reservaciones
                          </TableCell>
                        </TableRow>
                      ) : (
                        reservationHistory.map((reservation) => (
                          <StyledTableRow key={reservation._id}>
                            <TableCell onClick={() => handleNavigate(reservation._id)}>
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  color: '#1e88e5',
                                  cursor: 'pointer',
                                  fontSize: '0.9rem',
                                  '&:hover': { textDecoration: 'underline' },
                                }}
                              >
                                {reservation.roomNo}
                              </Typography>
                            </TableCell>
                            <TableCell>{new Date(reservation.checkInDate).toLocaleDateString('es-MX')}</TableCell>
                            <TableCell>{new Date(reservation.checkOutDate).toLocaleDateString('es-MX')}</TableCell>
                            <TableCell>${reservation.advanceAmount?.toLocaleString('es-MX')}</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>${(reservation.advanceAmount + reservation.totalAmount)?.toLocaleString('es-MX')}</TableCell>
                            <TableCell>${reservation.totalAmount?.toLocaleString('es-MX')}</TableCell>
                            <TableCell>
                              <StatusChip status={reservation.status} />
                            </TableCell>
                          </StyledTableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </StyledTableContainer>
              </InfoCard>
            </TabPanel>

            {/* ── Tab: Fidelidad (Fase 5.1) ── */}
            <TabPanel value="3" sx={{ p: 0 }}>
              <InfoCard elevation={0}>
                <SectionTitle>
                  <EmojiEventsIcon sx={{ color: '#ffa726', fontSize: '1.3rem' }} />
                  Programa de Fidelidad
                </SectionTitle>

                {loyaltyLoading ? (
                  <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2, mb: 2 }} />
                ) : (
                  <LoyaltyBadge tier={loyalty?.tier || 'Bronze'} points={loyalty?.totalPoints || 0} />
                )}

                <Box sx={{ mt: 3 }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      color: '#1565c0',
                      mb: 1.5,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Historial de puntos
                  </Typography>

                  {(loyalty?.events || []).length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4, color: '#9ca3af' }}>
                      <EmojiEventsIcon sx={{ fontSize: 48, color: '#d1d5db', mb: 1, display: 'block', mx: 'auto' }} />
                      Aún no ha acumulado puntos. Sus próximos consumos en el
                      restaurante o estadías sumarán automáticamente.
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {loyalty.events.map((ev) => (
                        <LoyaltyEventRow key={ev.eventId} event={ev} />
                      ))}
                    </Box>
                  )}
                </Box>
              </InfoCard>
            </TabPanel>
          </TabContext>
        </Box>
      </DashboardWrapper>
    </>
  );
}